import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext(null);

function parseTokenPayload(token) {
    if (!token || typeof token !== 'string') return null;
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        return JSON.parse(window.atob(normalized));
    } catch (error) {
        return null;
    }
}

function isStoredTokenValid(token) {
    if (!token) return false;
    const payload = parseTokenPayload(token);
    if (!payload) return false;
    if (!payload.exp) return true;
    const nowInSeconds = Math.floor(Date.now() / 1000);
    return payload.exp > nowInSeconds;
}

function clearStoredAuth() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
}

function parseStoredUser() {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        const parsedUser = JSON.parse(raw);
        return {
            User_ID: parsedUser.User_ID || '',
            First_Name: parsedUser.First_Name || '',
            Last_Name: parsedUser.Last_Name || '',
            Nickname: parsedUser.Nickname || '',
            Title: parsedUser.Title || '',
            Gender: parsedUser.Gender || '',
            Email_Address: parsedUser.Email_Address || '',
            Tel: parsedUser.Tel || '',
            User_Role: parsedUser.User_Role || 'Non_ATL_General',
            direct_marketing: parsedUser.direct_marketing || false,
            email_list: parsedUser.email_list || false,
            card_id: parsedUser.card_id || '',
            createdAt: parsedUser.createdAt || new Date().toISOString(),
            lastLogin: parsedUser.lastLogin || new Date().toISOString()
        };
    } catch (e) {
        console.error('Error parsing stored user:', e);
        return null;
    }
}

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(() =>
        isStoredTokenValid(localStorage.getItem('token'))
    );
    const [currentUser, setCurrentUser] = useState(() =>
        isStoredTokenValid(localStorage.getItem('token')) ? parseStoredUser() : null
    );

    useEffect(() => {
        // Ensure stale/invalid tokens don't keep the app in an authenticated UI state.
        const storedToken = localStorage.getItem('token');
        if (storedToken && !isStoredTokenValid(storedToken)) {
            clearStoredAuth();
            setIsAuthenticated(false);
            setCurrentUser(null);
        }

        // Listen for Supabase auth changes (e.g. session ended elsewhere)
        const {
            data: { subscription }
        } = supabase.auth.onAuthStateChange((event, session) => {
            // App auth uses the API JWT in localStorage. Supabase session is often empty on refresh;
            // treating !session as sign-out cleared login on every reload.
            if (event === 'SIGNED_OUT') {
                setIsAuthenticated(false);
                setCurrentUser(null);
                clearStoredAuth();
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        const enforceValidSession = () => {
            const token = localStorage.getItem('token');
            if (!token) {
                if (isAuthenticated || currentUser) {
                    setIsAuthenticated(false);
                    setCurrentUser(null);
                }
                return;
            }
            if (!isStoredTokenValid(token)) {
                clearStoredAuth();
                setIsAuthenticated(false);
                setCurrentUser(null);
                window.location.href = '/login';
            }
        };

        enforceValidSession();
        const intervalId = window.setInterval(enforceValidSession, 30000);
        window.addEventListener('focus', enforceValidSession);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', enforceValidSession);
        };
    }, [isAuthenticated, currentUser]);

    const login = async (email, password) => {
        try {
            // Call backend login endpoint which handles Supabase authentication
            // Use relative path in production (nginx will proxy to backend)
            // Use explicit localhost URL only for local development
            // In production, use relative URL which will be proxied by nginx
            const baseURL = ''; // Use relative path and let the proxy handle it (dev) or nginx (prod)
                
            console.log('🔍 Login attempt for:', email);
            console.log('🔍 API URL:', `${baseURL}/api/auth/login`);
            
            // Create AbortController for timeout (slightly longer to avoid first-login timeouts)
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
            
            let response;
            try {
                response = await fetch(`${baseURL}/api/auth/login`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ email, password }),
                    signal: controller.signal
                });
                clearTimeout(timeoutId);
            } catch (fetchError) {
                clearTimeout(timeoutId);
                if (fetchError.name === 'AbortError') {
                    throw new Error('Login request timed out. Please try again in a few seconds. If this keeps happening, please contact the system administrator.');
                }
                throw fetchError;
            }
            
            console.log('🔍 Login response status:', response.status);

            // Check if response is ok before parsing JSON
            if (!response.ok) {
                // Try to parse error message
                let errorData;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    // If response isn't JSON, return generic error
                    return {
                        success: false,
                        message: `Server error (${response.status}). Please check if the server is running.`
                    };
                }
                
                return {
                    success: false,
                    message: errorData.message || 'Login failed',
                    accountStatus: errorData.accountStatus
                };
            }

            const data = await response.json();
            console.log('🔍 Login response data:', { ...data, token: '[REDACTED]' });

            // Store token and user data
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            
            // Ensure all required fields are present with default values
            const completeUser = {
                User_ID: data.user.User_ID || '',
                First_Name: data.user.First_Name || '',
                Last_Name: data.user.Last_Name || '',
                Nickname: data.user.Nickname || '',
                Title: data.user.Title || '',
                Gender: data.user.Gender || '',
                Email_Address: data.user.Email_Address || '',
                Tel: data.user.Tel || '',
                User_Role: data.user.User_Role || 'Non_ATL_General',
                direct_marketing: data.user.direct_marketing || false,
                email_list: data.user.email_list || false,
                card_id: data.user.card_id || '',
                createdAt: data.user.createdAt || new Date().toISOString(),
                lastLogin: data.user.lastLogin || new Date().toISOString()
            };
            
            setCurrentUser(completeUser);
            setIsAuthenticated(true);
            
            return { success: true };
        } catch (error) {
            console.error('❌ Login error:', error);
            
            // Provide more specific error messages
            if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                return {
                    success: false,
                    message: 'Cannot connect to server. Please check if the backend server is running.'
                };
            }
            
            return {
                success: false,
                message: error.message || 'An error occurred. Please try again.'
            };
        }
    };

    const logout = async () => {
        try {
            // Sign out from Supabase
            await supabase.auth.signOut();
        } catch (error) {
            console.error('Supabase logout error:', error);
        }
        
        // Clear local storage
        clearStoredAuth();
        setIsAuthenticated(false);
        setCurrentUser(null);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, currentUser, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 